'use client';

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  savePatientQuestionnaireResponse,
  getQuestionnaireResponses,
} from '@/client/sdk.gen';
import {
  QuestionnaireResponseOut,
  expected_answer_format,
  QuestionnaireQuestion,
} from '@/client/types.gen';

export default function QuestionnairePage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] =
    useState<QuestionnaireQuestion | null>(null);
  const [currentAnswer, setCurrentAnswer] = useState<string>('');
  const [responses, setResponses] = useState<QuestionnaireResponseOut[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [answerFormat, setAnswerFormat] =
    useState<expected_answer_format>('text');
  const [questionImportance, setQuestionImportance] = useState<number>(3);

  // Load responses and current question
  useEffect(() => {
    if (!bookingId) {
      setError(
        'Missing booking ID. Please use the questionnaire link sent to you.'
      );
      setIsLoading(false);
      return;
    }

    // Fetch responses and get the current question
    fetchQuestionnaire();
  }, [bookingId]);

  const fetchQuestionnaire = async () => {
    if (!bookingId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getQuestionnaireResponses({
        path: { booking_id: bookingId },
      });

      if (response.data) {
        setResponses(response.data);

        // Sort responses by sequence to ensure proper order
        const sortedResponses = [...response.data].sort(
          (a, b) => a.sequence - b.sequence
        );

        // Find the first question with a null or empty answer, or get the last question
        const unansweredQuestion = sortedResponses.find(
          (q) => !q.answer || q.answer.trim() === ''
        );

        if (unansweredQuestion) {
          // We found an unanswered question - use this as the current question
          setCurrentQuestion({
            question: unansweredQuestion.question,
            sequence: unansweredQuestion.sequence,
          });

          // Set additional question metadata if available
          if (unansweredQuestion.expected_answer_format) {
            setAnswerFormat(
              unansweredQuestion.expected_answer_format as expected_answer_format
            );
          }

          if (unansweredQuestion.importance) {
            setQuestionImportance(unansweredQuestion.importance);
          }
        } else if (sortedResponses.length > 0) {
          // All questions have been answered
          setIsComplete(true);
        } else {
          // No questions found
          setError('No questionnaire found for this booking.');
        }

        // Calculate progress based on responses with answers
        if (sortedResponses.length > 0) {
          const answeredCount = sortedResponses.filter(
            (q) => q.answer && q.answer.trim() !== ''
          ).length;
          const totalCount = sortedResponses.length;
          setProgress(Math.min(100, (answeredCount / totalCount) * 100));
        }
      } else {
        setError('Failed to load the questionnaire. Please try again later.');
      }
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      setError('Failed to load the questionnaire. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Custom interface for the SavePatientQuestionnaireResponseResponse using the correct types
  interface QuestionnaireResponseApiResponse {
    saved_response: QuestionnaireResponseOut;
    next_question: QuestionnaireResponseOut | null;
    questionnaire_complete: boolean;
  }

  const handleSubmitAnswer = async () => {
    if (!bookingId || !currentQuestion) return;

    if (!currentAnswer.trim()) {
      setError('Please provide an answer before continuing.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Find the current unanswered question to get its ID
      const unansweredQuestion = responses.find(
        (q) =>
          q.question === currentQuestion.question &&
          q.sequence === currentQuestion.sequence
      );

      if (!unansweredQuestion?.id) {
        setError(
          'Could not identify the current question. Please refresh the page.'
        );
        setIsSubmitting(false);
        return;
      }

      const response = await savePatientQuestionnaireResponse({
        body: {
          booking_id: bookingId,
          answer: currentAnswer,
          questionnaire_id: unansweredQuestion.id,
        },
        path: { booking_id: bookingId },
      });

      // Clear the current answer
      setCurrentAnswer('');

      if (response.data) {
        // Cast the response to our expected structure
        const apiResponse = response.data as QuestionnaireResponseApiResponse;

        // Update the responses array with the saved response
        setResponses((prev) => {
          // Find and replace the updated response
          const updatedResponses = [...prev];
          const index = updatedResponses.findIndex(
            (r) => r.id === apiResponse.saved_response.id
          );

          if (index !== -1) {
            updatedResponses[index] = apiResponse.saved_response;
          } else {
            updatedResponses.push(apiResponse.saved_response);
          }

          return updatedResponses;
        });

        // Check if the questionnaire is complete
        if (apiResponse.questionnaire_complete) {
          setIsComplete(true);
        } else if (apiResponse.next_question) {
          // Set the next question (now using the full QuestionnaireResponseOut type)
          const nextQuestion = apiResponse.next_question;

          // Convert to QuestionnaireQuestion for state
          setCurrentQuestion({
            question: nextQuestion.question,
            sequence: nextQuestion.sequence,
          });

          // Set answer format and importance from the next question directly
          if (nextQuestion.expected_answer_format) {
            setAnswerFormat(
              nextQuestion.expected_answer_format as expected_answer_format
            );
          } else {
            setAnswerFormat('text');
          }

          if (nextQuestion.importance) {
            setQuestionImportance(nextQuestion.importance);
          } else {
            setQuestionImportance(3);
          }

          // Add the next question to responses if it's not already there
          setResponses((prev) => {
            if (!prev.some((r) => r.id === nextQuestion.id)) {
              return [...prev, nextQuestion];
            }
            return prev;
          });
        } else {
          // Fallback to fetching the full questionnaire if we don't have a next question
          await fetchQuestionnaire();
        }

        // Update progress
        const answeredCount =
          responses.filter((q) => q.answer && q.answer.trim() !== '').length +
          1;
        const totalCount = responses.length;
        setProgress(Math.min(100, (answeredCount / totalCount) * 100));
      } else {
        // Fallback to fetching the full questionnaire
        await fetchQuestionnaire();
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Failed to submit your answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAnswerInput = () => {
    switch (answerFormat) {
      case 'yes/no':
        return (
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="yes"
                name="yesno"
                value="Yes"
                checked={currentAnswer === 'Yes'}
                onChange={() => setCurrentAnswer('Yes')}
              />
              <Label htmlFor="yes">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="no"
                name="yesno"
                value="No"
                checked={currentAnswer === 'No'}
                onChange={() => setCurrentAnswer('No')}
              />
              <Label htmlFor="no">No</Label>
            </div>
          </div>
        );

      case 'scale':
        return (
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span>Not at all</span>
              <span>Moderate</span>
              <span>Severe</span>
            </div>
            <div className="flex justify-between gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <Button
                  key={num}
                  variant={
                    currentAnswer === num.toString() ? 'default' : 'outline'
                  }
                  className="w-8 h-8 p-0"
                  onClick={() => setCurrentAnswer(num.toString())}
                >
                  {num}
                </Button>
              ))}
            </div>
          </div>
        );

      default: // text
        return (
          <Textarea
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            placeholder="Type your answer here..."
            className="min-h-[100px]"
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p>Loading your questionnaire...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center">
              Questionnaire Complete
            </CardTitle>
            <CardDescription className="text-center">
              Thank you for completing the questionnaire
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center pt-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <p className="mt-4">
              Your answers have been submitted. Your healthcare provider now has
              the information they need for your appointment.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <CardTitle>Medical Questionnaire</CardTitle>
          <CardDescription>
            Please answer these questions to help your healthcare provider
          </CardDescription>
          <div className="h-2 w-full bg-gray-200 rounded-full mt-2">
            <div
              className="h-2 bg-primary rounded-full"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentQuestion && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div
                  className={`px-2 py-1 rounded-md text-xs font-medium ${
                    questionImportance >= 4
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : questionImportance >= 3
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  }`}
                >
                  {questionImportance >= 4
                    ? 'Important'
                    : questionImportance >= 3
                    ? 'Moderate'
                    : 'General'}
                </div>
                <div className="text-sm text-muted-foreground">
                  Question {currentQuestion.sequence}
                </div>
              </div>

              <h3 className="text-lg font-medium">
                {currentQuestion.question}
              </h3>

              {renderAnswerInput()}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            disabled={
              responses.filter((r) => r.answer && r.answer.trim() !== '')
                .length === 0 || isSubmitting
            }
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <Button
            onClick={handleSubmitAnswer}
            disabled={isSubmitting || !currentAnswer.trim()}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
